import { CSSProperties, memo, ReactNode, useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import type { RowData } from '@tanstack/react-table';
import type { DataTableAlign, IDataTableColumn } from '../DataTable.types';
import type { DataTableRow } from '../dataTableFeatures';
import { samePropsByRowData } from './samePropsByRowData';

/** Содержимое и события ячейки — из корня таблицы, через ref (см. IDataTableBodyRowRenderer). */
export interface IDataTableBodyCellRenderer<T extends RowData> {
    renderContent: (props: IDataTableBodyCellProps<T>) => ReactNode;
    renderExpandButton: (row: DataTableRow<T>, open: boolean, loading: boolean) => ReactNode;
    stickyClass: (colId: string) => string | undefined;
    stickyStyle: (colId: string) => CSSProperties | undefined;
    /** Скрипт ячейки потребителя (`onCellRendered`): зовётся после каждого рендера ячейки. */
    onCellElement: ((props: IDataTableBodyCellProps<T>, element: HTMLElement) => void) | undefined;
    startEdit: (rowId: string, colId: string) => void;
}

export interface IDataTableBodyCellProps<T extends RowData> {
    row: DataTableRow<T>;
    colId: string;
    col: IDataTableColumn<T>;
    value: unknown;
    colIndex: number | undefined;
    align: DataTableAlign;
    editable: boolean;
    editing: boolean;
    /** Ячейка-дерево: отступ уровня и кнопка разворота рядом со значением. */
    treeLabel: boolean;
    /** Отступ уровня дерева, px. */
    indent: number;
    canExpand: boolean;
    expanded: boolean;
    loading: boolean;
    /** Версия вида всех ячеек: колонки, раскладка закреплённых зон, скрипт ячейки. */
    view: object;
    /** Версия вида ячейки: `cellVersion` таблицы, без него — `rowVersion` строки. */
    version?: string | number;
    renderer: { readonly current: IDataTableBodyCellRenderer<T> };
}

function DataTableBodyCell<T extends RowData>(props: IDataTableBodyCellProps<T>) {
    const { row, colId, col, colIndex, align, editable, editing, treeLabel, renderer } = props;
    const api = renderer.current;
    const content = api.renderContent(props);
    const elementRef = useRef<HTMLDivElement>(null);
    // Скрипт ячейки — после каждого её рендера (memo пропускает рендеры, не трогающие ячейку), без ref-колбэка,
    // который React отцеплял и цеплял заново на каждом рендере. Без скрипта зависимость постоянна (null):
    // эффект отрабатывает один раз при монтировании, а не после каждого рендера каждой ячейки.
    const scripted = api.onCellElement ? props : null;
    useLayoutEffect(() => {
        if (scripted && elementRef.current) renderer.current.onCellElement?.(scripted, elementRef.current);
    }, [scripted, renderer]);
    return (
        // клик/Enter по редактируемой ячейке открывает редактор; клавиатурная цель —
        // сама ячейка (tabIndex), это осознанный паттерн cell-editing
        <div
            data-col={colId}
            role="cell"
            aria-colindex={colIndex}
            ref={elementRef}
            className={clsx(
                'dtb__cell',
                align !== 'left' && `dtb__cell--${align}`,
                editable && 'dtb__cell--editable',
                editing && 'dtb__cell--editing',
                api.stickyClass(colId),
                col.wrapMode && `dtb__cell--wm-${col.wrapMode}`,
            )}
            style={api.stickyStyle(colId)}
            tabIndex={editable && !editing ? 0 : undefined}
            onClick={editable && !editing ? () => renderer.current.startEdit(row.id, colId) : undefined}
            onKeyDown={
                editable && !editing
                    ? (e) => {
                          if ((e.key === 'Enter' || e.key === 'F2') && e.target === e.currentTarget) {
                              e.preventDefault();
                              renderer.current.startEdit(row.id, colId);
                          }
                      }
                    : undefined
            }
        >
            {treeLabel ? (
                <div
                    className={clsx('dtb__tree-label', props.canExpand && 'is-parent')}
                    style={{ marginLeft: props.indent }}
                >
                    {props.canExpand ? (
                        api.renderExpandButton(row, props.expanded, props.loading)
                    ) : (
                        // место кнопки у листьев: значения соседних строк стоят в одной колонке
                        <span className="dtb__expand-slot" aria-hidden />
                    )}
                    <span className="dtb__tree-label-text">{content}</span>
                </div>
            ) : (
                content
            )}
        </div>
    );
}

export default memo(DataTableBodyCell, samePropsByRowData) as typeof DataTableBodyCell;
